import * as React from 'react'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

const HEX_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/

const normalizeHex = (raw: string) => {
  const trimmed = raw.trim()
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`
}

const clampChannel = (v: number) => Math.max(0, Math.min(255, Math.round(v)))
const clampPercent = (v: number) => Math.max(0, Math.min(100, v))

const hexToRgb = (hex: string) => {
  const normalized = hex.replace('#', '').slice(0, 6)
  if (normalized.length === 3) {
    return {
      r: parseInt(normalized[0] + normalized[0], 16),
      g: parseInt(normalized[1] + normalized[1], 16),
      b: parseInt(normalized[2] + normalized[2], 16),
    }
  }
  if (normalized.length === 6) {
    return {
      r: parseInt(normalized.slice(0, 2), 16),
      g: parseInt(normalized.slice(2, 4), 16),
      b: parseInt(normalized.slice(4, 6), 16),
    }
  }
  return null
}

const parseAlpha = (hex: string) => {
  const normalized = hex.replace('#', '')
  if (normalized.length === 8) {
    return Math.round((parseInt(normalized.slice(6, 8), 16) / 255) * 100)
  }
  return 100
}

const rgbToHex = (r: number, g: number, b: number) => {
  const toHex = (v: number) =>
    clampChannel(v).toString(16).padStart(2, '0').toUpperCase()
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

const alphaToHex = (alpha: number) => {
  const v = Math.round(clampPercent(alpha) * 2.55)
  return v.toString(16).padStart(2, '0').toUpperCase()
}

const formatRgb = (hex: string) => {
  const rgb = hexToRgb(hex)
  const a = parseAlpha(hex)
  if (!rgb) return hex
  return a < 100
    ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})`
    : `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`
}

interface HSV {
  h: number
  s: number
  v: number
}

const rgbToHsv = (r: number, g: number, b: number): HSV => {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const d = max - min
  let h = 0
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60
    else if (max === gn) h = ((bn - rn) / d + 2) * 60
    else h = ((rn - gn) / d + 4) * 60
  }
  return {
    h,
    s: max === 0 ? 0 : (d / max) * 100,
    v: max * 100,
  }
}

const hsvToRgb = ({ h, s, v }: HSV) => {
  const sn = s / 100
  const vn = v / 100
  const c = vn * sn
  const hp = (((h % 360) + 360) % 360) / 60
  const x = c * (1 - Math.abs((hp % 2) - 1))
  let r1 = 0
  let g1 = 0
  let b1 = 0
  if (hp < 1) [r1, g1, b1] = [c, x, 0]
  else if (hp < 2) [r1, g1, b1] = [x, c, 0]
  else if (hp < 3) [r1, g1, b1] = [0, c, x]
  else if (hp < 4) [r1, g1, b1] = [0, x, c]
  else if (hp < 5) [r1, g1, b1] = [x, 0, c]
  else [r1, g1, b1] = [c, 0, x]
  const m = vn - c
  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  }
}

const hsvToHex = (hsv: HSV) => {
  const { r, g, b } = hsvToRgb(hsv)
  return rgbToHex(r, g, b)
}

const hexToHsv = (hex: string): HSV => {
  const rgb = hexToRgb(hex) ?? { r: 0, g: 0, b: 0 }
  return rgbToHsv(rgb.r, rgb.g, rgb.b)
}

const CHECKER_BG = {
  backgroundImage:
    'linear-gradient(45deg, #555 25%, transparent 25%), linear-gradient(-45deg, #555 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #555 75%), linear-gradient(-45deg, transparent 75%, #555 75%)',
  backgroundSize: '8px 8px',
  backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0',
}

export type ColorFormat = 'hex' | 'rgb'

export interface ColorPickerProps {
  value?: string
  onChange?: (color: string) => void
  onBlur?: () => void
  disabled?: boolean
  name?: string
  className?: string
  isInvalid?: boolean
  align?: 'start' | 'center' | 'end'
  format?: ColorFormat
  withAlpha?: boolean
}

export const ColorPicker = React.forwardRef<
  HTMLButtonElement,
  ColorPickerProps
>(
  (
    {
      value = '#000000',
      onChange,
      onBlur,
      disabled,
      name,
      className,
      isInvalid,
      align = 'start',
      format = 'rgb',
      withAlpha = true,
    },
    ref,
  ) => {
    const [hexInput, setHexInput] = React.useState(
      value?.replace(/^#/, '').slice(0, 6) ?? '',
    )
    const [hsv, setHsv] = React.useState<HSV>(() => hexToHsv(value))
    const [alpha, setAlpha] = React.useState<number>(() => parseAlpha(value))

    React.useEffect(() => {
      const baseHex = value?.replace(/^#/, '').slice(0, 6) ?? ''
      setHexInput(baseHex)
      const incoming = hexToHsv(value)
      const incomingAlpha = parseAlpha(value)
      const currentHex = composeHex(hsv, alpha)
      if (currentHex.toUpperCase() !== value.toUpperCase()) {
        setHsv((prev) => ({
          h: incoming.s === 0 ? prev.h : incoming.h,
          s: incoming.s,
          v: incoming.v,
        }))
        setAlpha(incomingAlpha)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    const rgb = hsvToRgb(hsv)
    const baseHex = hsvToHex(hsv)

    function composeHex(currHsv: HSV, currAlpha: number) {
      const base = hsvToHex(currHsv)
      return withAlpha && currAlpha < 100
        ? `${base}${alphaToHex(currAlpha)}`
        : base
    }

    const emit = (nextHsv: HSV, nextAlpha: number) => {
      setHsv(nextHsv)
      setAlpha(nextAlpha)
      onChange?.(composeHex(nextHsv, nextAlpha))
    }

    const commitHex = (raw: string) => {
      const next = normalizeHex(raw)
      if (HEX_REGEX.test(next)) {
        const upper = next.toUpperCase()
        const composed =
          withAlpha && alpha < 100 ? `${upper}${alphaToHex(alpha)}` : upper
        onChange?.(composed)
      } else {
        setHexInput(value?.replace(/^#/, '').slice(0, 6) ?? '')
      }
    }

    const updateChannel = (channel: 'r' | 'g' | 'b', raw: string) => {
      if (raw === '') return
      const num = parseInt(raw, 10)
      if (Number.isNaN(num)) return
      const next = { ...rgb, [channel]: clampChannel(num) }
      emit(rgbToHsv(next.r, next.g, next.b), alpha)
    }

    const updateAlpha = (raw: string) => {
      if (raw === '') return
      const num = parseInt(raw, 10)
      if (Number.isNaN(num)) return
      emit(hsv, clampPercent(num))
    }

    const svRef = React.useRef<HTMLDivElement>(null)
    const hueRef = React.useRef<HTMLDivElement>(null)
    const alphaRef = React.useRef<HTMLDivElement>(null)

    const handleSvPointer = (e: React.PointerEvent<HTMLDivElement>) => {
      const rect = svRef.current?.getBoundingClientRect()
      if (!rect) return
      const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left))
      const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top))
      emit(
        {
          h: hsv.h,
          s: (x / rect.width) * 100,
          v: (1 - y / rect.height) * 100,
        },
        alpha,
      )
    }

    const handleHuePointer = (e: React.PointerEvent<HTMLDivElement>) => {
      const rect = hueRef.current?.getBoundingClientRect()
      if (!rect) return
      const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left))
      emit({ ...hsv, h: (x / rect.width) * 360 }, alpha)
    }

    const handleAlphaPointer = (e: React.PointerEvent<HTMLDivElement>) => {
      const rect = alphaRef.current?.getBoundingClientRect()
      if (!rect) return
      const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left))
      emit(hsv, (x / rect.width) * 100)
    }

    const startDrag =
      (handler: (e: React.PointerEvent<HTMLDivElement>) => void) =>
      (e: React.PointerEvent<HTMLDivElement>) => {
        if (disabled) return
        e.currentTarget.setPointerCapture(e.pointerId)
        handler(e)
      }

    const onDragMove =
      (handler: (e: React.PointerEvent<HTMLDivElement>) => void) =>
      (e: React.PointerEvent<HTMLDivElement>) => {
        if (disabled || e.buttons !== 1) return
        handler(e)
      }

    const triggerLabel = format === 'rgb' ? formatRgb(value) : value

    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            ref={ref}
            type="button"
            name={name}
            disabled={disabled}
            onBlur={onBlur}
            className={cn(
              'flex h-[44px] w-full items-center gap-3 rounded-[16px] border border-monochrome-120 bg-monochrome-3-60 px-3 text-button-m text-monochrome-1 transition-colors hover:border-accent-1 focus-visible:border-accent-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
              isInvalid && '!border-accent-3',
              className,
            )}
          >
            <div
              className="rounded-full w-[24px] h-[24px] border border-monochrome-120 flex-shrink-0"
              style={{
                background: value,
              }}
            />
            <span className="font-mono text-button-m tracking-wider">
              {triggerLabel}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align={align}
          sideOffset={8}
          className="relative w-72 border-monochrome-2 bg-white p-3 text-monochrome-5 shadow-xl rounded-2xl"
        >
          <svg
            width="14"
            height="7"
            viewBox="0 0 14 7"
            className="absolute -top-[7px] left-[10%] -translate-x-1/2"
            aria-hidden
          >
            <path d="M0 7 L7 0 L14 7 Z" fill="white" />
          </svg>
          <div className="space-y-3">
            <div
              ref={svRef}
              onPointerDown={startDrag(handleSvPointer)}
              onPointerMove={onDragMove(handleSvPointer)}
              className={cn(
                'relative h-44 w-full overflow-hidden rounded-lg border border-monochrome-2/60 touch-none select-none',
                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-crosshair',
              )}
              style={{
                backgroundColor: `hsl(${hsv.h}, 100%, 50%)`,
                backgroundImage:
                  'linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent)',
              }}
            >
              <div
                className="pointer-events-none absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.4)]"
                style={{
                  left: `${hsv.s}%`,
                  top: `${100 - hsv.v}%`,
                  backgroundColor: baseHex,
                }}
              />
            </div>

            <div>
              <p className="mb-1 text-xs text-monochrome-5/60">Hue</p>
              <div
                ref={hueRef}
                onPointerDown={startDrag(handleHuePointer)}
                onPointerMove={onDragMove(handleHuePointer)}
                className={cn(
                  'relative h-2.5 w-full rounded-full touch-none select-none',
                  disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                )}
                style={{
                  background:
                    'linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)',
                }}
              >
                <div
                  className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.4)]"
                  style={{
                    left: `${(hsv.h / 360) * 100}%`,
                    backgroundColor: `hsl(${hsv.h}, 100%, 50%)`,
                  }}
                />
              </div>
            </div>

            {withAlpha && (
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <p className="text-xs text-monochrome-5/60">Opacity</p>
                  <p className="text-xs text-monochrome-5/60">
                    {Math.round(alpha)}%
                  </p>
                </div>
                <div
                  ref={alphaRef}
                  onPointerDown={startDrag(handleAlphaPointer)}
                  onPointerMove={onDragMove(handleAlphaPointer)}
                  className={cn(
                    'relative h-2.5 w-full overflow-hidden rounded-full touch-none select-none',
                    disabled
                      ? 'cursor-not-allowed opacity-50'
                      : 'cursor-pointer',
                  )}
                  style={CHECKER_BG}
                >
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: `linear-gradient(to right, transparent, ${baseHex})`,
                    }}
                  />
                  <div
                    className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.4)]"
                    style={{
                      left: `${alpha}%`,
                      backgroundColor: baseHex,
                    }}
                  />
                </div>
              </div>
            )}

            <div
              className={cn(
                'grid gap-1.5',
                withAlpha
                  ? 'grid-cols-[1.6fr_1fr_1fr_1fr_1fr]'
                  : 'grid-cols-[1.6fr_1fr_1fr_1fr]',
              )}
            >
              <ChannelInput label="Hex" mono>
                <input
                  type="text"
                  value={hexInput}
                  maxLength={6}
                  disabled={disabled}
                  spellCheck={false}
                  onChange={(e) => {
                    const next = e.target.value
                      .replace(/[^0-9A-Fa-f]/g, '')
                      .toUpperCase()
                    setHexInput(next)
                    if (next.length === 6 || next.length === 3) {
                      const fullHex = `#${next}`
                      onChange?.(
                        withAlpha && alpha < 100
                          ? `${fullHex}${alphaToHex(alpha)}`
                          : fullHex,
                      )
                    }
                  }}
                  onBlur={() => commitHex(hexInput)}
                  className="h-7 w-full bg-transparent text-center font-mono text-xs uppercase tracking-wider text-monochrome-5 outline-none disabled:cursor-not-allowed"
                  placeholder="000000"
                />
              </ChannelInput>
              {(['r', 'g', 'b'] as const).map((channel) => (
                <ChannelInput key={channel} label={channel.toUpperCase()}>
                  <input
                    type="number"
                    min={0}
                    max={255}
                    value={rgb[channel]}
                    disabled={disabled}
                    onChange={(e) => updateChannel(channel, e.target.value)}
                    className="h-7 w-full bg-transparent text-center font-mono text-xs text-monochrome-5 outline-none disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  />
                </ChannelInput>
              ))}
              {withAlpha && (
                <ChannelInput label="A">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={Math.round(alpha)}
                    disabled={disabled}
                    onChange={(e) => updateAlpha(e.target.value)}
                    className="h-7 w-full bg-transparent text-center font-mono text-xs text-monochrome-5 outline-none disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  />
                </ChannelInput>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    )
  },
)
ColorPicker.displayName = 'ColorPicker'

interface ChannelInputProps {
  label: string
  children: React.ReactNode
  mono?: boolean
}

function ChannelInput({ label, children }: ChannelInputProps) {
  return (
    <div className="flex flex-col items-center">
      <span className="mb-1 text-[10px] uppercase tracking-wider text-monochrome-5/60">
        {label}
      </span>
      <div className="flex h-9 w-full items-center rounded-md border border-monochrome-2/60 bg-white px-1">
        {children}
      </div>
    </div>
  )
}
